import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

export const useRequestClaim = (requestType) => {
  const [claimedRequests, setClaimedRequests] = useState(new Map());
  const [pollingInterval, setPollingInterval] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const checkClaimStatus = useCallback(async (requestId) => {
    // This will be called by parent component after fetching requests
    // to update claimed status from server response
  }, []);

  const claimRequest = async (requestId) => {
    // Prevent double-click
    if (isProcessing) {
      toast.warning('Sedang memproses request sebelumnya, mohon tunggu...');
      return false;
    }

    const token = localStorage.getItem('admin_token');
    setIsProcessing(true);
    
    try {
      const response = await axios.post(
        `${API}/api/admin/requests/${requestType}/${requestId}/claim`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000 // 30 second timeout - increased from 10s to handle backend processing time
        }
      );
      
      // Check if response has success field
      if (response.data?.success === true) {
        toast.success(response.data.message || 'Request berhasil diklaim');
        return true;
      } else {
        // Backend returned but without success flag
        toast.error('Response tidak valid dari server');
        return false;
      }
    } catch (error) {
      console.error('[CLAIM ERROR]', error);
      
      // Handle specific HTTP status codes
      if (error.response?.status === 409) {
        // Already claimed by someone else
        toast.error(error.response.data.detail || 'Request sudah diklaim admin lain');
        return false;
      } else if (error.response?.status === 404) {
        toast.error('Request tidak ditemukan');
        return false;
      } else if (error.response?.status === 401) {
        toast.error('Session expired, silakan login kembali');
        return false;
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        // Timeout - might have succeeded on backend
        toast.warning('Request memakan waktu lama - mohon tunggu beberapa saat dan refresh halaman untuk memastikan status');
        return false;
      } else if (!error.response) {
        // Network error
        toast.error('Koneksi bermasalah - mohon coba lagi');
        return false;
      } else {
        // Other errors
        toast.error(error.response?.data?.detail || 'Gagal mengklaim request');
        return false;
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const releaseRequest = async (requestId) => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.post(
        `${API}/api/admin/requests/${requestType}/${requestId}/release`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success(response.data.message || 'Request berhasil direlease');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal merelease request');
      return false;
    }
  };

  const forceReleaseRequest = async (requestId) => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.post(
        `${API}/api/admin/requests/${requestType}/${requestId}/force-release`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success(response.data.message || 'Request berhasil di-force release');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal force release request');
      return false;
    }
  };

  const isClaimedByMe = (request) => {
    const currentAdminUsername = localStorage.getItem('admin_username');
    return request?.claimed_by_username === currentAdminUsername;
  };

  const isClaimedByOther = (request) => {
    const currentAdminUsername = localStorage.getItem('admin_username');
    return request?.claimed_by && request?.claimed_by_username !== currentAdminUsername;
  };

  const getClaimTimeElapsed = (claimedAt) => {
    if (!claimedAt) return null;
    
    const claimTime = new Date(claimedAt);
    const now = new Date();
    const diffMs = now - claimTime;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'baru saja';
    if (diffMins < 60) return `${diffMins} menit yang lalu`;
    
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours} jam yang lalu`;
  };

  return {
    claimRequest,
    releaseRequest,
    forceReleaseRequest,
    isClaimedByMe,
    isClaimedByOther,
    getClaimTimeElapsed,
    isProcessing
  };
};
